'use client';

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import type { ArticleItem } from '@/lib/contentful/types';
import { extractPlainText } from '@/utils/extractPlainText';

function normalize(s: string) {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

export function useArticleFiltering(
  articles: ArticleItem[],
  featuredArticles: ArticleItem[],
) {
  const all = useMemo(() => {
    const seen = new Set<string>();
    return [...featuredArticles, ...articles]
      .filter((a) => {
        if (seen.has(a.slug)) return false;
        seen.add(a.slug);
        return true;
      })
      .sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  }, [articles, featuredArticles]);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [activeTags, setActiveTags] = useState<Set<string>>(
    () => new Set(searchParams.getAll('tag')),
  );
  const [search, setSearch] = useState('');

  const tagCounts = useMemo(() => {
    const map = new Map<string, number>();
    all.forEach((a) => {
      if (a.tag?.label) map.set(a.tag.label, (map.get(a.tag.label) ?? 0) + 1);
    });
    return map;
  }, [all]);

  const tags = useMemo(() => Array.from(tagCounts.keys()), [tagCounts]);

  useEffect(() => {
    const params = new URLSearchParams();
    activeTags.forEach((tag) => params.append('tag', tag));
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [activeTags, pathname, router]);

  const filtered = useMemo(() => {
    let result = all;
    if (activeTags.size > 0) {
      result = result.filter(
        (a) => a.tag?.label && activeTags.has(a.tag.label),
      );
    }
    if (search.trim()) {
      const q = normalize(search);
      result = result.filter(
        (a) =>
          normalize(extractPlainText(a.title)).includes(q) ||
          normalize(a.summary ?? '').includes(q),
      );
    }
    return result;
  }, [all, activeTags, search]);

  function handleTagToggle(tag: string) {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
        // if all tags are selected, reset back to "all"
        if (next.size === tags.length) return new Set();
      }
      return next;
    });
  }

  function handleClearTags() {
    setActiveTags(new Set());
  }

  function handleSearchChange(value: string) {
    setSearch(value);
  }

  return {
    all,
    filtered,
    tags,
    tagCounts,
    activeTags,
    search,
    handleTagToggle,
    handleClearTags,
    handleSearchChange,
  };
}
